/**
 * The editable panel for the selected node: title, description, kind, colour,
 * group, the host's pointer choice (FlowMapProps.refChoices) for the kinds it
 * names, the nested checklist, links, outgoing arrows, and delete.
 *
 * Every field writes through the model's immutable edits (updateNode,
 * setNodeParent, removeNodes) via `env.edit`, which applies them to the LATEST
 * document, so two quick edits never overwrite each other. Text is read in
 * `env.locale` and written with writeText(prev, value, env.textLocale): the
 * page builder edits one language of a bilingual map, the CRM plain strings.
 * Every picker is the widgets' own pick-one list (`../combo/`), never a native select.
 */
import { useId, useState, type ReactElement } from "react";
import { Combo } from "../combo/index.js";
import { nodeTitle } from "./canvasModel.js";
import { NodeConnections } from "./EdgeInspector.js";
import { IconTrash, IconX } from "./icons.js";
import { AddText, CommitText, Field, IconButton, Section, ToneChips, patcher, type EditorEnv, type Patch } from "./inspectorFields.js";
import { LinksEditor } from "./LinkEditor.js";
import { FLOW_KIND_LABEL } from "./labels.js";
import {
  FLOW_LIMITS,
  FLOW_NODE_KINDS,
  makeFlowId,
  readText,
  removeNodes,
  setNodeParent,
  updateNode,
  writeText,
  type FlowNode,
  type FlowNodeKind,
  type FlowStep,
  type NodePatch,
} from "./model.js";

function withDone(step: FlowStep, done: boolean): FlowStep {
  const out: FlowStep = { id: step.id, text: step.text };
  if (done) out.done = true;
  return out;
}

function StepsEditor({ env, node, patch }: { env: EditorEnv; node: FlowNode; patch: Patch }): ReactElement {
  const { labels, locale, textLocale, textDefaultLocale } = env;
  const steps = node.steps ?? [];
  const done = steps.filter((s) => s.done).length;
  const setSteps = (fn: (list: FlowStep[]) => FlowStep[]): void =>
    patch((n) => {
      const next = fn(n.steps ?? []);
      return { steps: next.length ? next : null };
    });
  return (
    <Section title={labels.steps} aside={steps.length ? <span className="wa-fm__count">{labels.stepsProgress(done, steps.length)}</span> : null}>
      {steps.length ? (
        <ul className="wa-fm__rows">
          {steps.map((s) => {
            const text = readText(s.text, locale);
            return (
              <li key={s.id} className="wa-fm__row">
                <input
                  type="checkbox"
                  className="wa-fm__check"
                  checked={s.done === true}
                  aria-label={text}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setSteps((list) => list.map((x) => (x.id === s.id ? withDone(x, on) : x)));
                  }}
                />
                <CommitText
                  value={text}
                  ariaLabel={labels.steps}
                  maxLength={FLOW_LIMITS.text}
                  onCommit={(v) => {
                    const t = v.trim();
                    // An emptied line is a removed line.
                    setSteps((list) =>
                      t ? list.map((x) => (x.id === s.id ? { ...x, text: writeText(x.text, t, textLocale, textDefaultLocale) } : x)) : list.filter((x) => x.id !== s.id),
                    );
                  }}
                />
                <IconButton label={labels.remove} onClick={() => setSteps((list) => list.filter((x) => x.id !== s.id))}>
                  <IconX size={14} />
                </IconButton>
              </li>
            );
          })}
        </ul>
      ) : null}
      {steps.length < FLOW_LIMITS.stepsPerNode ? (
        <AddText
          label={labels.addStep}
          placeholder={labels.stepPlaceholder}
          maxLength={FLOW_LIMITS.text}
          onAdd={(t) =>
            setSteps((list) => [...list, { id: makeFlowId("s", new Set(list.map((x) => x.id)), true), text: writeText(undefined, t, textLocale) }])
          }
        />
      ) : null}
    </Section>
  );
}


/** The host's pointer as a choice (FlowMapProps.refChoices): "none", or one of the host's options. */
function RefChoiceField({ env, node, patch }: { env: EditorEnv; node: FlowNode; patch: Patch }): ReactElement | null {
  const ids = useId();
  const choices = env.refChoices;
  if (!choices) return null;
  const current = node.ref?.type === choices.type ? node.ref.id : "";
  // A pointer the host no longer offers stays visible as itself rather than reading as "none".
  const unknown = current !== "" && !choices.options.some((o) => o.id === current);
  return (
    <Field label={choices.label} htmlFor={`${ids}-ref`}>
      <Combo
        id={`${ids}-ref`}
        className="wa-fm__pickw"
        buttonClassName="wa-fm__pick"
        fitPanel
        label={choices.label}
        value={current}
        choices={[
          { value: "", label: choices.noneLabel },
          ...(unknown ? [{ value: current, label: current }] : []),
          ...choices.options.map((o) => ({ value: o.id, label: o.label })),
        ]}
        searchLabel={env.labels.pickSearch}
        emptyText={env.labels.pickNoMatch}
        onChange={(id) => patch(() => ({ ref: id ? { type: choices.type, id } : null }))}
      />
    </Field>
  );
}

export interface NodeInspectorProps {
  env: EditorEnv;
  node: FlowNode;
  /** Focus the title (a node that was just added). */
  autoFocusTitle?: boolean;
  onDeleted: () => void;
}

export function NodeInspector({ env, node, autoFocusTitle, onDeleted }: NodeInspectorProps): ReactElement {
  const { doc, labels, locale, textLocale, textDefaultLocale } = env;
  const ids = useId();
  const patch = patcher(env, node.id);
  const pill = node.kind === "start" || node.kind === "end";
  const isGroup = node.kind === "group";
  const groups = doc.nodes.filter((n) => n.kind === "group");
  // Pills and groups do not show a checklist or links on the card; keep the editors for data that is already there.
  const showSteps = (!pill && !isGroup) || (node.steps?.length ?? 0) > 0;
  const showLinks = (!pill && !isGroup) || (node.links?.length ?? 0) > 0;
  return (
    <>
      <Field label={labels.title} htmlFor={`${ids}-title`}>
        <CommitText
          id={`${ids}-title`}
          value={readText(node.title, locale)}
          placeholder={isGroup ? labels.kindGroup : labels.untitled}
          maxLength={FLOW_LIMITS.title}
          autoFocus={autoFocusTitle}
          onCommit={(v) => patch((n) => ({ title: writeText(n.title, v.trim(), textLocale, textDefaultLocale) }))}
        />
      </Field>
      <Field label={labels.description} htmlFor={`${ids}-desc`}>
        <CommitText
          id={`${ids}-desc`}
          multiline
          value={readText(node.description, locale)}
          maxLength={FLOW_LIMITS.text}
          onCommit={(v) => patch((n) => ({ description: writeText(n.description, v.trim(), textLocale, textDefaultLocale) }))}
        />
      </Field>
      <div className="wa-fm__row wa-fm__row--fields">
        <Field label={labels.kind} htmlFor={`${ids}-kind`}>
          <Combo
            id={`${ids}-kind`}
            className="wa-fm__pickw"
            buttonClassName="wa-fm__pick"
            fitPanel
            label={labels.kind}
            value={node.kind}
            choices={FLOW_NODE_KINDS.map((k) => ({ value: k, label: labels[FLOW_KIND_LABEL[k]] }))}
            onChange={(value) => {
              const kind = value as FlowNodeKind;
              patch(() => ({ kind }));
            }}
          />
        </Field>
        {!isGroup && groups.length ? (
          <Field label={labels.parent} htmlFor={`${ids}-parent`}>
            <Combo
              id={`${ids}-parent`}
              className="wa-fm__pickw"
              buttonClassName="wa-fm__pick"
              fitPanel
              label={labels.parent}
              value={node.parentId ?? ""}
              choices={[{ value: "", label: labels.noParent }, ...groups.map((g) => ({ value: g.id, label: nodeTitle(g, locale, labels) }))]}
              searchLabel={labels.pickSearch}
              emptyText={labels.pickNoMatch}
              onChange={(value) => {
                const parent = value || null;
                env.edit((d) => setNodeParent(d, node.id, parent));
              }}
            />
          </Field>
        ) : null}
      </div>
      {env.refChoices && env.refChoices.kinds.includes(node.kind) ? <RefChoiceField env={env} node={node} patch={patch} /> : null}
      <ToneChips value={node.tone} labels={labels} onChange={(tone) => patch(() => ({ tone }))} />
      {showSteps ? <StepsEditor env={env} node={node} patch={patch} /> : null}
      {showLinks ? <LinksEditor env={env} node={node} patch={patch} /> : null}
      <NodeConnections env={env} node={node} />
      <button
        type="button"
        className="wa-fm__btn wa-fm__btn--danger wa-fm__btn--block"
        onClick={() => {
          if (env.edit((d) => removeNodes(d, [node.id]))) onDeleted();
        }}
      >
        <IconTrash size={15} />
        {labels.deleteNode}
      </button>
    </>
  );
}
